import { useMemo, useState } from "react";

import {
	formatCarrierLiability,
	matchesServiceLevelFilter,
} from "~/features/quote/utils/carrier-selection-utils.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type {
	CarrierFiltersType,
	FilterKey,
	SortDirection,
	SortType,
	UseCarrierFiltersReturn,
} from "~/shared/types/carrier-selection-types";

const initialFilters: CarrierFiltersType = {
	currentQuote: null,
	currentServiceLevel: [],
	currentTransitDays: [],
	currentServiceType: "",
	currentPrice: null,
	currentLiability: [],
};

export const filterCarrierPrices = (
	carrierPrices: CarrierPrice[],
	filters: CarrierFiltersType,
): CarrierPrice[] => {
	// Separate saved carriers (id > 0) from ad hoc carriers (id <= 0)
	const savedCarriers = carrierPrices.filter((carrier) => carrier.id > 0);
	const adHocCarriers = carrierPrices.filter((carrier) => carrier.id <= 0);

	// Apply filters only to saved carriers
	let filteredSaved = [...savedCarriers];

	if (filters.currentQuote !== null) {
		filteredSaved = filteredSaved.filter(
			(carrierPrice) => carrierPrice.quoteId === filters.currentQuote,
		);
	}

	if (filters.currentServiceLevel.length > 0) {
		filteredSaved = filteredSaved.filter((carrierPrice) =>
			matchesServiceLevelFilter(carrierPrice, filters.currentServiceLevel),
		);
	}

	if (filters.currentTransitDays.length > 0) {
		filteredSaved = filteredSaved.filter(
			(carrierPrice) =>
				carrierPrice.transitDays !== null &&
				filters.currentTransitDays.includes(carrierPrice.transitDays),
		);
	}

	if (filters.currentServiceType !== "") {
		filteredSaved = filteredSaved.filter(
			(carrierPrice) => carrierPrice.serviceType === filters.currentServiceType,
		);
	}

	if (filters.currentPrice) {
		const [minPrice, maxPrice] = filters.currentPrice;
		filteredSaved = filteredSaved.filter((carrierPrice) => {
			const price = carrierPrice.customerRate ?? 0;
			return price >= minPrice && price <= maxPrice;
		});
	}

	if (filters.currentLiability.length > 0) {
		filteredSaved = filteredSaved.filter((carrierPrice) => {
			const liabilityLabel = formatCarrierLiability(carrierPrice);
			return filters.currentLiability.includes(liabilityLabel);
		});
	}

	// Return ad hoc carriers (unfiltered) combined with filtered saved carriers
	return [...adHocCarriers, ...filteredSaved];
};

const getServiceTypeSortValue = (serviceLevelId: number): number =>
	serviceLevelId > 1 && serviceLevelId <= 6 ? serviceLevelId : 100;

const compareDelivery = (a: CarrierPrice, b: CarrierPrice): number => {
	const transitA = a.transitDays ?? 0;
	const transitB = b.transitDays ?? 0;
	if (transitA !== transitB) {
		return transitA > transitB ? 1 : -1;
	}

	const serviceTypeSortA = getServiceTypeSortValue(a.serviceLevelId);
	const serviceTypeSortB = getServiceTypeSortValue(b.serviceLevelId);
	if (serviceTypeSortA !== serviceTypeSortB) {
		return serviceTypeSortA > serviceTypeSortB ? 1 : -1;
	}

	const nameA = a.carrier.displayName ?? "";
	const nameB = b.carrier.displayName ?? "";
	if (nameA !== nameB) {
		return nameA > nameB ? 1 : -1;
	}

	return 0;
};

export const sortCarrierPrices = (
	carrierPrices: CarrierPrice[],
	sortType: SortType,
	sortDirection: SortDirection = "asc",
): CarrierPrice[] => {
	// Separate ad hoc carriers (id <= 0) from saved carriers (id > 0)
	// Ad hoc carriers should not be sorted, regardless of completeness
	const adHocCarriers = carrierPrices.filter((carrier) => carrier.id <= 0);
	const sortableCarriers = carrierPrices.filter((carrier) => carrier.id > 0);

	if (sortableCarriers.length > 0) {
		switch (sortType) {
			case "rate": {
				// Sort by rate only (lowest to highest by default)
				sortableCarriers.sort((a, b) => {
					const rateA = a.customerRate ?? 0;
					const rateB = b.customerRate ?? 0;
					return sortDirection === "asc" ? rateA - rateB : rateB - rateA;
				});

				break;
			}
			case "delivery": {
				// Sort carriers by Delivery Time, then service type sort value, then carrier name
				sortableCarriers.sort((a, b) => {
					const result = compareDelivery(a, b);
					return sortDirection === "asc" ? result : -result;
				});

				break;
			}
			case "preferred": {
				// Sort carriers by Preferred/COTY status, then by rate within each group
				sortableCarriers.sort((a, b) => {
					const aPreferred =
						a.carrier.isPreferred || a.carrier.isCarrierOfTheYear ? 1 : 0;
					const bPreferred =
						b.carrier.isPreferred || b.carrier.isCarrierOfTheYear ? 1 : 0;
					const preferredDiff =
						sortDirection === "asc"
							? bPreferred - aPreferred
							: aPreferred - bPreferred;
					if (preferredDiff !== 0) return preferredDiff;
					// Sub-sort by customer rate (lowest to highest) within each group
					return (a.customerRate ?? 0) - (b.customerRate ?? 0);
				});

				break;
			}
			// No default
		}
	}
	// Return ad hoc carriers (unsorted) followed by sorted saved carriers
	return [...adHocCarriers, ...sortableCarriers];
};

export const useCarrierFilters = (): UseCarrierFiltersReturn => {
	const [filters, setFilters] = useState<CarrierFiltersType>(initialFilters);
	const [currentTab, setCurrentTab] = useState(0);
	const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
	const tabMapping: Record<number, SortType> = {
		0: "rate",
		1: "delivery",
		2: "preferred",
	};

	const sortType: SortType = tabMapping[currentTab] ?? "rate";

	const updateFilter = <K extends FilterKey>(
		key: K,
		value: CarrierFiltersType[K],
	) => {
		setFilters((prevFilters) => ({
			...prevFilters,
			[key]: value,
		}));
	};

	const clearFilters = () => {
		setFilters(initialFilters);
	};

	const getFilteredAndSortedCarriers = useMemo(
		() => (carrierPrices: CarrierPrice[]) => {
			const filtered = filterCarrierPrices(carrierPrices, filters);
			return sortCarrierPrices(filtered, sortType, sortDirection);
		},
		[filters, sortType, sortDirection],
	);

	return {
		// State
		filters,
		currentTab,
		sortType,
		sortDirection,

		// Actions
		updateFilter,
		setCurrentTab,
		setSortDirection,
		clearFilters,

		// Computed values
		getFilteredAndSortedCarriers,
	};
};
